import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, Megaphone, Eye, EyeOff } from "lucide-react";

const empty = { message: "", link_url: "", link_label: "", style: "info", is_active: true };
const STYLES = [
  { v: "info", label: "Blue (informational)" },
  { v: "urgent", label: "Red (urgent)" },
  { v: "celebration", label: "Gold (celebration)" },
];

export default function AdminAnnouncements() {
  const { authedClient } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/announcements");
      setItems(data);
    } catch { toast.error("Could not load announcements"); } finally { setLoading(false); }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error("Message is required");
    try {
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/announcements", form);
        setItems([data, ...items]);
        toast.success("Posted");
      } else {
        const { data } = await authedClient().patch(`/admin/announcements/${editing}`, form);
        setItems(items.map(x => x.id === editing ? data : x));
        toast.success("Saved");
      }
      setEditing(null); setForm(empty);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const toggleActive = async (a) => {
    try {
      const { data } = await authedClient().patch(`/admin/announcements/${a.id}`, { is_active: !a.is_active });
      setItems(items.map(x => x.id === a.id ? data : x));
    } catch { toast.error("Could not toggle"); }
  };
  const remove = async (a) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await authedClient().delete(`/admin/announcements/${a.id}`);
      setItems(items.filter(x => x.id !== a.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-announcements-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ CMS</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Announcements</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            A short banner shown at the very top of the website. Only the most-recently-updated active one is displayed.
          </p>
        </div>
        {!editing && (
          <button onClick={() => { setForm(empty); setEditing("new"); }} className="btn-primary" data-testid="admin-ann-new">
            <Plus className="w-4 h-4" /> New announcement
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-ann-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">{editing === "new" ? "New" : "Edit"} announcement</h2>
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }}><X className="w-5 h-5" /></button>
          </div>
          <Field label="Message *">
            <textarea required rows={2} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={ic} data-testid="ann-form-message" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Link URL (optional)"><input type="url" value={form.link_url || ""} onChange={e => setForm({...form, link_url: e.target.value})} className={ic} /></Field>
            <Field label="Link label"><input value={form.link_label || ""} onChange={e => setForm({...form, link_label: e.target.value})} className={ic} placeholder="Learn more" /></Field>
            <Field label="Style">
              <select value={form.style} onChange={e => setForm({...form, style: e.target.value})} className={ic}>
                {STYLES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Active?">
              <label className="inline-flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="accent-[color:var(--blue)]" />
                Show this announcement on the site
              </label>
            </Field>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="ann-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
        : items.length === 0 ? <p className="text-center text-[color:var(--muted)] py-8">No announcements yet.</p>
        : (
          <div className="space-y-3">
            {items.map(a => (
              <div key={a.id} className={`p-4 rounded-xl border ${a.is_active ? "border-[color:var(--line)] bg-white" : "border-dashed border-[color:var(--line)] bg-[color:var(--cream)]/50 opacity-70"}`} data-testid={`admin-ann-row-${a.id}`}>
                <div className="flex items-start gap-3">
                  <Megaphone className={`w-5 h-5 mt-1 ${a.style === "urgent" ? "text-red-600" : a.style === "celebration" ? "text-amber-600" : "text-[color:var(--blue-deep)]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[color:var(--blue-ink)] font-medium">{a.message}</div>
                    {a.link_url && <div className="text-xs text-[color:var(--muted)] mt-1">{a.link_label || "Link"} → {a.link_url}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => toggleActive(a)} className="p-1.5 rounded hover:bg-[color:var(--cream)]" title={a.is_active ? "Active" : "Inactive"}>
                      {a.is_active ? <Eye className="w-4 h-4 text-[color:var(--blue-deep)]" /> : <EyeOff className="w-4 h-4 text-[color:var(--muted)]" />}
                    </button>
                    <button onClick={() => { setForm({...empty, ...a}); setEditing(a.id); }} className="btn-ghost text-xs py-1 px-2">Edit</button>
                    <button onClick={() => remove(a)} className="p-1.5 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

const ic = "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">{label}</label>{children}</div>;
}
